import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Switch, Empty } from 'antd';
import {
  createTableChangeHandler,
  createScrollSync,
  createPaginationConfig,
} from '@client/utils/table/tableUtils';
import {
  createTableColumns,
  createExpandedRowRender,
} from '@client/feature/activeSubstance/config/tableConfig';

const ResultItems = ({ data, onPageChange, onSortChange, currentSorting }) => {
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const tableWrapperRef = useRef(null);
  const tableHeaderRef = useRef(null);

  const results = data?.results || [];
  const pageSize = data?.size || 20;

  const filteredResults = useMemo(
    () =>
      showSelectedOnly
        ? results.filter((item) => selectedRowKeys.includes(item.id))
        : results,
    [showSelectedOnly, results, selectedRowKeys]
  );

  const columns = useMemo(
    () => createTableColumns(t, currentSorting),
    [t, currentSorting]
  );

  const handleTableChange = useMemo(
    () => createTableChangeHandler(onPageChange, onSortChange, columns),
    [onPageChange, onSortChange, columns]
  );

  const expandedRowRender = useMemo(() => createExpandedRowRender(), []);

  useEffect(() => {
    const tableHeader = tableHeaderRef.current;
    const tableBody = document.querySelector('.ant-table-content');
    return createScrollSync(tableHeader, tableBody);
  }, [data?.page, filteredResults]);

  useEffect(() => {
    const currentPageKeys = filteredResults.map((item) => item.id);
    setExpandedRowKeys(currentPageKeys);
  }, [filteredResults]);

    useEffect(() => {
  if (showSelectedOnly && selectedRowKeys.length === 0) {
    setShowSelectedOnly(false);
  }
}, [selectedRowKeys, showSelectedOnly]);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: setSelectedRowKeys,
    }),
    [selectedRowKeys]
  );

  const pagination = useMemo(
    () => createPaginationConfig(data, pageSize, t),
    [data, pageSize, t]
  );

  if (!results.length) {
    return (
      <div className="text-center py-8">
        <Empty
          description={t('active-substance|No results found')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Switch
          checked={showSelectedOnly}
          onChange={setShowSelectedOnly}
          disabled={selectedRowKeys.length === 0}
        />
        <span>{t('common|Show only my selections')}</span>
      </div>

      <div className="responsive-table-wrapper" ref={tableWrapperRef}>
        <Table
          scroll={{ x: 'max-content' }}
          className="custom-table"
          columns={columns}
          dataSource={filteredResults}
          rowKey="id"
          rowClassName="custom-row"
          rowSelection={rowSelection}
          pagination={pagination}
          onChange={handleTableChange}
          loading={!data}
          hideOnSinglePage
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            onExpand: (expanded, record) => {
              setExpandedRowKeys((prev) =>
                expanded
                  ? [...prev, record.id]
                  : prev.filter((key) => key !== record.id)
              );
            },
            expandIcon: () => null,
            expandIconColumnIndex: -1,
          }}
        />
      </div>
    </div>
  );
};

export default ResultItems;